import React, { ChangeEvent, Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import isEmpty from 'lodash/isEmpty';
import { ScrollToFormikError } from '../elements/ScrollToFormikError';
import { FormikErrors } from 'formik';
import { motion } from "framer-motion";

export interface IInputSelectMenuItem {
  id?: string;
  name: string;
  value: string | number | null;
}

export interface IInputSelectMenuProps {
  selectInputClassName?: string;
  label?: string;
  listSelectedValue?: number | string | null;
  showAvatar?: boolean;
  search?: boolean;
  onChange: Dispatch<SetStateAction<IInputSelectMenuItem | null>> | any;
  onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
  list?: Array<string | number | any>;
  grouped?: boolean;
  groupedList?: Record<string, Array<IInputSelectMenuItem>>;
  placeholder?: string;
  description?: JSX.Element | React.ReactElement | string | number | any;
  name: string;
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[] | undefined;
  disabled?: boolean;
  required?: boolean;
  helperText?: string | string[] | FormikErrors<any> | FormikErrors<any>[] | undefined;
  sort?: boolean;
  emptyListMessage?: string;
  searchPlaceHolder?: string;
  wrapperClasses?: string;
  editable?: boolean;
  labelClasses?: string;
}

function InputSelectMenu<T>({
  selectInputClassName,
  label,
  listSelectedValue,
  onChange,
  list = [],
  description,
  name,
  error,
  disabled = false,
  required = false,
  helperText,
  onBlur,
  sort = false,
  searchPlaceHolder = 'Search ...',
  wrapperClasses,
  editable = true,
  labelClasses
}: IInputSelectMenuProps): JSX.Element {
  const [searchValue, setSearchValue] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const [searchResult, setSearchResult] = useState<Array<string | number>>([]);
  const fieldRef = useRef<HTMLDivElement>(null);

  const searchedList =
    isEmpty(searchResult) && searchValue.length < 1 ? list : searchResult;

  const onSearchChange = (evt: ChangeEvent<HTMLInputElement> | undefined) => {
    const val = evt?.target.value ?? '';
    onChange(val);

    setSearchValue(val);
    const srchList: Array<string | number> = [];

    list.forEach((listItem) => {
      if (listItem.toString().toLowerCase().includes(val)) srchList.push(listItem);
    });
    setSearchResult(srchList);
  };

  useEffect(() => {
    if (listSelectedValue) {
      setSearchValue(String(listSelectedValue));
    }
  }, [listSelectedValue])

  return (
    <Listbox
      ref={fieldRef}
      value={listSelectedValue}
      onChange={(value: any) => {
        onChange(value);
        setShowDropdown(false);
        setSearchValue("");
      }}
      disabled={disabled}
      as={'div'}
      className={clsx({ ['animate-wiggle']: error && required }, wrapperClasses)}>
      {({ open }) => (
        <>
          {/* Label */}
          {label && (
            <Listbox.Label
              className={clsx('block text-sm font-medium text-gray-700 dark:text-darkBg-mainText', {
                ['!text-danger-main']: error && required
              }, labelClasses)}>
              {label} <span className="!text-danger-main">{required && '*'}</span>
              {description && (
                <p
                  className={clsx('text-xs text-gray-400 dark:text-darkBg-subText italic', {
                    ['!text-danger-border']: error && required
                  })}>
                  {description}
                </p>
              )}
            </Listbox.Label>
          )}

          <div className="mt-1 relative">
            {/* Input Field */}
            <div
              className={clsx(
                'relative w-full bg-white dark:bg-darkBg border border-gray-300 dark:border-darkBg-border shadow-sm text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-main focus:border-primary-border sm:text-sm rounded-full',
                { ['bg-red-200 !fixedborder-danger-main focus:ring-danger-main focus:border-danger-border']: error && required }, selectInputClassName)}
              onClick={(e: any) => {
                e.preventDefault();
                // if (!open) {
                setShowDropdown(!showDropdown);
                // }
              }}
            >
              <input
                type="text"
                name="search"
                id="search"
                className="border border-gray-300 dark:border-darkBg-border focus:ring-transparent focus:outline-none focus:border-gray-300 dark:focus:border-darkBg-border rounded-full block w-full text-md sm:text-sm bg-white dark:bg-darkBg dark:text-darkBg-mainText"
                placeholder={searchPlaceHolder}
                value={listSelectedValue ?? ""}
                onChange={(e) => onSearchChange(e)}
                disabled={disabled}
                autoComplete={"off"}
              />

              {!disabled && (
                <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 border-none" onClick={() => {
                  setShowDropdown(!showDropdown);
                }}>
                  {showDropdown ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-darkBg-subText" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-darkBg-subText" aria-hidden="false" />
                  )}
                </span>
              )}
            </div>

            <Transition
              show={showDropdown}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute z-40 mt-1 w-full bg-white dark:border dark:border-darkBg-border dark:bg-darkBg shadow-lg max-h-96 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" static>
                {/* List */}
                {searchedList && !isEmpty(searchedList) && (
                  <>
                    <div className="px-2 pb-2 pt-1">
                      <>
                        {(sort ? searchedList.sort() : searchedList).map((listItem, key) => (
                          <Listbox.Option
                            key={key}
                            className={({ active, selected }) =>
                              clsx(
                                active ? 'bg-primary-surface dark:bg-primary-surface' : 'text-gray-900 dark:text-darkBg-mainText hover:bg-gray-100 dark:hover:bg-gray-700',
                                'cursor-default select-none relative py-2 pl-3 pr-0 text-sm',
                                { ['bg-primary-main']: selected }
                              )
                            }
                            value={listItem}>
                            {({ selected }) => (
                              <>
                                <div className="flex items-center">
                                  <span
                                    className={clsx(
                                      selected ? 'font-semibold' : 'font-normal',
                                      'ml-3 block truncate text-sm'
                                    )}>
                                    {listItem}
                                  </span>
                                </div>

                                {selected ? (
                                  <span
                                    className={clsx(
                                      selected ? 'text-white' : 'text-gray-900',
                                      'absolute inset-y-0 right-0 flex items-center pr-1'
                                    )}>
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </>
                    </div>
                  </>
                )}
              </Listbox.Options>
            </Transition>
            {/* Helper Text */}
            <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: error })}>
                {helperText && (
                    <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center font-medium"
                    >
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {helperText && typeof helperText === 'string' ? helperText : undefined}
                    </motion.p>
                )}
            </div>
          </div>
          <ScrollToFormikError
            fieldName={name ?? ""}
            fieldRef={fieldRef}
          />
        </>
      )}
    </Listbox>
  );
}

export default InputSelectMenu;